import React, { ReactNode } from 'react';
import styled from 'styled-components';

import {
  FlexRow,
  formatHash,
  HashLength,
  Size,
  useMatchMedia,
  TruncateBox,
} from '@make-software/cspr-ui';
import { Tooltip, BodyText } from '@block-explorer/ui';

import { CopyButton } from '../copy-button/copy-button';

const StyledWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const LabelContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 4,
}));

const InputContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 16px',
  height: 36,
  backgroundColor: theme.styleguideColors.backgroundPrimary,
  border: theme.border.base,
  borderRadius: theme.borderRadius.base,
}));

const SuffixContainer = styled('div')(({ theme }) => ({
  marginLeft: 16,
}));

const SuffixTextContainer = styled(SuffixContainer)(({ theme }) => ({
  color: theme.styleguideColors.contentSecondary,
}));

const PrefixContainer = styled('div')(({ theme }) => ({
  marginRight: 16,
}));

export enum TextRowType {
  'Text' = 'text',
  'Hash' = 'hash',
  'Number' = 'number',
}

export interface TextRowProps {
  label?: string;
  rightLabel?: string;
  text?: string | null;
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  suffixText?: string;
  type: TextRowType;
  labelIcon?: ReactNode;
  labelTooltipText?: string | undefined;
}
/**
 * @example
```
<TextRow
  label={t('Account Address')}
  text={publicKey}
  type={TextRowType.Hash}
/>
```
 */
export function TextRow(props: TextRowProps) {
  const {
    label,
    rightLabel,
    text,
    prefixIcon,
    suffixText,
    suffixIcon,
    type,
    labelIcon,
    labelTooltipText,
  } = props;

  const responsiveHashSize = useMatchMedia(
    [HashLength.SMALL, HashLength.MEDIUM, HashLength.LARGE, HashLength.FULL],
    []
  );
  const responsiveTruncateSize = useMatchMedia<Size>([2, 3, 4, 5], []);

  return (
    <StyledWrapper>
      <LabelContainer>
        {label && (
          <Tooltip title={labelTooltipText} limitWidth>
            <FlexRow gap={3} align={'center'}>
              <BodyText scale={'sm'} size={1}>
                {label}
              </BodyText>
              {labelIcon}
            </FlexRow>
          </Tooltip>
        )}
        {rightLabel && (
          <BodyText scale={'sm'} size={1}>
            {rightLabel}
          </BodyText>
        )}
      </LabelContainer>
      <InputContainer>
        {prefixIcon && <PrefixContainer>{prefixIcon}</PrefixContainer>}
        <FlexRow itemsSpacing={4}>
          {type === TextRowType.Hash ? (
            <Tooltip title={text}>
              <BodyText size={3} monotype={true}>
                {formatHash(text || '', responsiveHashSize)}
              </BodyText>
            </Tooltip>
          ) : (
            <TruncateBox size={responsiveTruncateSize}>
              <Tooltip title={text}>
                <BodyText size={3} monotype={type === TextRowType.Number}>
                  {text}
                </BodyText>
              </Tooltip>
            </TruncateBox>
          )}
          <CopyButton value={text} />
        </FlexRow>
        {suffixIcon && <SuffixContainer>{suffixIcon}</SuffixContainer>}
        {suffixText && (
          <SuffixTextContainer>
            <BodyText size={3}>{suffixText}</BodyText>
          </SuffixTextContainer>
        )}
      </InputContainer>
    </StyledWrapper>
  );
}

export default TextRow;
